import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { projectService } from '../../services/projectService';
import { courseService } from '../../services/courseService';
import LoadingSpinner from '../common/LoadingSpinner';

const ProjectConfig = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSuccess, showError } = useApp();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [availableCourses, setAvailableCourses] = useState([]);
  const [selectedCourseToAdd, setSelectedCourseToAdd] = useState('');

  // Datos básicos del proyecto
  const [formData, setFormData] = useState({
    title: '',
    startDate: '',
    endDate: '',
    courses: [],
    generalObjective: '',
    expectedProduct: '',
    planningDeadline: '',
    thinkingSkill: '',
    // Campos de asignaturas
    evaluationTypes: {
      autoevaluacion: false,
      coevaluacion: false
    },
    projectSubjects: [],
    maxIndicatorScore: '',
    isIntegrated: false
  });

  // Habilidades con sus sub-habilidades
  const [skills, setSkills] = useState({
    describir: {
      enabled: false,
      subSkills: []
    },
    explicar: {
      enabled: false,
      subSkills: []
    },
    practicar: {
      enabled: false,
      subSkills: []
    },
    especificar: {
      enabled: false,
      subSkills: []
    },
    formular: {
      enabled: false,
      subSkills: []
    },
    criticar: {
      enabled: false,
      subSkills: []
    }
  });

  // Indicaciones para apoderados
  const [parentInstructions, setParentInstructions] = useState('');

  // Opciones para habilidades de pensamiento
  const thinkingSkillOptions = [
    'FUNDAMENTAR',
    'ANALIZAR',
    'SINTETIZAR',
    'EVALUAR',
    'CREAR',
    'APLICAR'
  ];

  // Sub-habilidades disponibles por cada habilidad principal
  const subSkillsOptions = {
    describir: ['Identificar características', 'Enumerar elementos', 'Detallar procesos', 'Clasificar información'],
    explicar: ['Relacionar causas', 'Interpretar datos', 'Justificar ideas', 'Clarificar conceptos'],
    practicar: ['Ejecutar procedimientos', 'Aplicar técnicas', 'Demostrar habilidades', 'Ejercitar destrezas'],
    especificar: ['Definir parámetros', 'Delimitar alcances', 'Precisar detalles', 'Concretar aspectos'],
    formular: ['Plantear hipótesis', 'Diseñar soluciones', 'Proponer alternativas', 'Construir modelos'],
    criticar: ['Cuestionar supuestos', 'Argumentar posiciones', 'Evaluar evidencias', 'Debatir perspectivas']
  };

  useEffect(() => {
    loadProject();
    loadCourses();
  }, [id]);

  const loadCourses = async () => {
    try {
      const response = await courseService.getCourses();
      if (response.success) {
        setAvailableCourses(response.data);
      }
    } catch (error) {
      console.error('Error al cargar cursos:', error);
    }
  };

  const loadProject = async () => {
    try {
      setLoading(true);
      const response = await projectService.getProjectById(id);
      if (response.success) {
        const project = response.data;
        setFormData({
          title: project.title || '',
          startDate: project.startDate || '',
          endDate: project.endDate || '',
          courses: project.courses || [],
          generalObjective: project.generalObjective || '',
          expectedProduct: project.expectedProduct || '',
          planningDeadline: project.planningDeadline || '',
          thinkingSkill: project.thinkingSkill || '',
          evaluationTypes: project.evaluationTypes || {
            autoevaluacion: false,
            coevaluacion: false
          },
          projectSubjects: project.projectSubjects || [],
          maxIndicatorScore: project.maxIndicatorScore || '',
          isIntegrated: project.isIntegrated || false
        });

        if (project.skills) {
          setSkills(project.skills);
        }

        if (project.parentInstructions) {
          setParentInstructions(project.parentInstructions);
        }
      } else {
        showError('Error al cargar el proyecto');
        navigate('/projects');
      }
    } catch (error) {
      showError('Error de conexión al cargar el proyecto');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSkillToggle = (skillName) => {
    setSkills(prev => ({
      ...prev,
      [skillName]: {
        ...prev[skillName],
        enabled: !prev[skillName].enabled,
        subSkills: !prev[skillName].enabled ? [] : prev[skillName].subSkills
      }
    }));
  };

  const handleSubSkillToggle = (skillName, subSkill) => {
    setSkills(prev => {
      const currentSubSkills = prev[skillName].subSkills;
      const newSubSkills = currentSubSkills.includes(subSkill)
        ? currentSubSkills.filter(s => s !== subSkill)
        : [...currentSubSkills, subSkill];

      return {
        ...prev,
        [skillName]: {
          ...prev[skillName],
          subSkills: newSubSkills
        }
      };
    });
  };

  const handleAddCourse = () => {
    if (!selectedCourseToAdd) {
      showError('Seleccione un curso para agregar');
      return;
    }

    const currentCourses = formData.courses || [];
    if (currentCourses.includes(selectedCourseToAdd)) {
      showError('Este curso ya está agregado al proyecto');
      return;
    }

    setFormData(prev => ({
      ...prev,
      courses: [...currentCourses, selectedCourseToAdd]
    }));

    setSelectedCourseToAdd('');
  };

  const handleRemoveCourse = (courseName) => {
    setFormData(prev => ({
      ...prev,
      courses: (prev.courses || []).filter(c => c !== courseName)
    }));
  };

  const handleEvaluationTypeToggle = (type) => {
    setFormData(prev => ({
      ...prev,
      evaluationTypes: {
        ...prev.evaluationTypes,
        [type]: !prev.evaluationTypes[type]
      }
    }));
  };

  const handleSubjectToggle = (subjectName) => {
    setFormData(prev => {
      const currentSubjects = prev.projectSubjects || [];
      const existingSubject = currentSubjects.find(s => s.name === subjectName);

      if (existingSubject) {
        // Remove subject
        return {
          ...prev,
          projectSubjects: currentSubjects.filter(s => s.name !== subjectName)
        };
      } else {
        // Add subject
        return {
          ...prev,
          projectSubjects: [...currentSubjects, {
            name: subjectName,
            hasEvaluation: false,
            hasAutoevaluacion: false,
            hasCoevaluacion: false
          }]
        };
      }
    });
  };

  const handleSubjectEvaluationToggle = (subjectName) => {
    setFormData(prev => ({
      ...prev,
      projectSubjects: prev.projectSubjects.map(s =>
        s.name === subjectName ? { ...s, hasEvaluation: !s.hasEvaluation } : s
      )
    }));
  };

  const handleSubjectAutoevaluacionToggle = (subjectName) => {
    setFormData(prev => ({
      ...prev,
      projectSubjects: prev.projectSubjects.map(s =>
        s.name === subjectName ? { ...s, hasAutoevaluacion: !s.hasAutoevaluacion } : s
      )
    }));
  };

  const handleSubjectCoevaluacionToggle = (subjectName) => {
    setFormData(prev => ({
      ...prev,
      projectSubjects: prev.projectSubjects.map(s =>
        s.name === subjectName ? { ...s, hasCoevaluacion: !s.hasCoevaluacion } : s
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones
    if (!formData.title.trim()) {
      showError('El título del proyecto es obligatorio');
      return;
    }

    if (!formData.startDate || !formData.endDate) {
      showError('Las fechas de inicio y fin son obligatorias');
      return;
    }

    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      showError('La fecha de fin no puede ser anterior a la fecha de inicio');
      return;
    }

    try {
      setSaving(true);
      const configData = {
        ...formData,
        skills,
        parentInstructions
      };

      const response = await projectService.updateProjectConfig(id, configData);

      if (response.success) {
        showSuccess('Configuración guardada exitosamente');
        navigate(`/projects/${id}`);
      } else {
        showError(response.message || 'Error al guardar la configuración');
      }
    } catch (error) {
      showError('Error de conexión al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen message="Cargando configuración..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configurar Proyecto</h1>
          <p className="text-gray-600">Configura los detalles y habilidades del proyecto ABP</p>
        </div>
        <button
          onClick={() => navigate(`/projects/${id}`)}
          className="text-gray-600 hover:text-gray-900 flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          <span>Cancelar</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              type="button"
              onClick={() => setActiveTab('general')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'general'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              General
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('asignaturas')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'asignaturas'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Asignaturas
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('habilidades')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'habilidades'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Habilidades
            </button>
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Tab: General */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">Información del Proyecto</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Título del Proyecto */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Título del Proyecto *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Ingrese el título del proyecto"
                    required
                  />
                </div>

                {/* Fecha de Inicio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha de Inicio *
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Fecha de Fin */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha de Fin *
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Cursos */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cursos *
                  </label>

                  {/* Selector y botón agregar */}
                  <div className="flex gap-2 mb-4">
                    <select
                      value={selectedCourseToAdd}
                      onChange={(e) => setSelectedCourseToAdd(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Seleccione un curso</option>
                      {availableCourses.map((course) => {
                        const courseName = `${course.level}${course.section ? ' ' + course.section : ''}`;
                        return (
                          <option key={course.id} value={courseName}>
                            {courseName}
                          </option>
                        );
                      })}
                    </select>
                    <button
                      type="button"
                      onClick={handleAddCourse}
                      className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span>Agregar</span>
                    </button>
                  </div>

                  {/* Tabla de cursos seleccionados */}
                  {formData.courses && formData.courses.length > 0 ? (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Curso
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Acción
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {formData.courses.map((courseName, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {courseName}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveCourse(courseName)}
                                  className="text-red-600 hover:text-red-900 text-sm font-medium"
                                >
                                  Quitar
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg p-4 text-center text-sm text-gray-500">
                      No hay cursos agregados al proyecto
                    </div>
                  )}
                </div>

                {/* Objetivo General */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Objetivo General
                  </label>
                  <textarea
                    name="generalObjective"
                    value={formData.generalObjective}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Describa el objetivo general del proyecto"
                  />
                </div>

                {/* Producto Esperado */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Producto Esperado
                  </label>
                  <input
                    type="text"
                    name="expectedProduct"
                    value={formData.expectedProduct}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Ej: Maqueta, presentación, informe, prototipo, etc."
                  />
                </div>

                {/* Fecha Tope Planificación */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha Tope Planificación
                  </label>
                  <input
                    type="date"
                    name="planningDeadline"
                    value={formData.planningDeadline}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                {/* Indicaciones para Apoderados */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Indicaciones para Apoderados
                  </label>
                  <textarea
                    value={parentInstructions}
                    onChange={(e) => setParentInstructions(e.target.value)}
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Escriba las indicaciones o instrucciones que los apoderados deben conocer sobre este proyecto..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tab: Asignaturas */}
          {activeTab === 'asignaturas' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">Configuración de Asignaturas</h2>

              {/* Asignaturas del Proyecto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Asignaturas del Proyecto
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  Seleccione las asignaturas que participan en el proyecto e indique si tienen evaluación
                </p>

                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                          Seleccionar
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Asignatura
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                          Tiene Evaluación
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                          Autoevaluación
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                          Coevaluación
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {['Matemáticas', 'Lenguaje', 'Ciencias Naturales', 'Historia', 'Inglés', 'Educación Física', 'Artes', 'Tecnología', 'Música'].map((subject) => {
                        const isSelected = formData.projectSubjects.find(s => s.name === subject);
                        return (
                          <tr key={subject} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={!!isSelected}
                                onChange={() => handleSubjectToggle(subject)}
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                              />
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {subject}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                disabled={!isSelected}
                                checked={isSelected?.hasEvaluation || false}
                                onChange={() => handleSubjectEvaluationToggle(subject)}
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              />
                            </td>
                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                disabled={!isSelected}
                                checked={isSelected?.hasAutoevaluacion || false}
                                onChange={() => handleSubjectAutoevaluacionToggle(subject)}
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              />
                            </td>
                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                disabled={!isSelected}
                                checked={isSelected?.hasCoevaluacion || false}
                                onChange={() => handleSubjectCoevaluacionToggle(subject)}
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Proyecto Integrado */}
              <div className="max-w-md">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Proyecto Integrado
                </label>
                <select
                  name="isIntegrated"
                  value={formData.isIntegrated}
                  onChange={(e) => setFormData(prev => ({ ...prev, isIntegrated: e.target.value === 'true' }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="false">No</option>
                  <option value="true">Sí</option>
                </select>
              </div>
            </div>
          )}

          {/* Tab: Habilidades */}
          {activeTab === 'habilidades' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">Habilidades del Proyecto</h2>

              {/* Habilidad de Pensamiento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Habilidad de Pensamiento
                </label>
                <select
                  name="thinkingSkill"
                  value={formData.thinkingSkill}
                  onChange={handleInputChange}
                  className="w-full md:w-1/2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Seleccione una habilidad</option>
                  {thinkingSkillOptions.map(skill => (
                    <option key={skill} value={skill}>{skill}</option>
                  ))}
                </select>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-4">
                  Seleccione las habilidades que se desarrollarán en este proyecto y sus sub-habilidades específicas
                </p>
              </div>

              <div className="space-y-6">
                {Object.keys(skills).map((skillName) => (
                  <div key={skillName} className="border border-gray-200 rounded-lg p-4">
                    {/* Checkbox de Habilidad Principal */}
                    <div className="flex items-center mb-3">
                      <input
                        type="checkbox"
                        id={`skill-${skillName}`}
                        checked={skills[skillName].enabled}
                        onChange={() => handleSkillToggle(skillName)}
                        className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <label
                        htmlFor={`skill-${skillName}`}
                        className="ml-3 text-sm font-medium text-gray-900 capitalize cursor-pointer"
                      >
                        {skillName}
                      </label>
                    </div>

                    {/* Sub-habilidades */}
                    {skills[skillName].enabled && (
                      <div className="ml-8 grid grid-cols-1 md:grid-cols-2 gap-2">
                        {subSkillsOptions[skillName].map((subSkill) => (
                          <div key={subSkill} className="flex items-center">
                            <input
                              type="checkbox"
                              id={`subskill-${skillName}-${subSkill}`}
                              checked={skills[skillName].subSkills.includes(subSkill)}
                              onChange={() => handleSubSkillToggle(skillName, subSkill)}
                              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                            <label
                              htmlFor={`subskill-${skillName}-${subSkill}`}
                              className="ml-2 text-sm text-gray-700 cursor-pointer"
                            >
                              {subSkill}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Botones de Acción */}
          <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(`/projects/${id}`)}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors duration-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Guardando...</span>
                </>
              ) : (
                <span>Guardar Configuración</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectConfig;
